import React from 'react';
import { PencilRuler, PenTool, HardHat } from 'lucide-react';
import './NameProjectFields.css';

type DesignerOption = { uid: string; displayName: string };

type EngineeringFirm = {
  id: string;
  name: string;
  engineers: Array<{ id: string; name: string; email?: string }>;
};

export type ProjectDealTeamFieldsValue = {
  designerUid: string;
  architectName: string;
  architectUids: string[];
  engineerFirmId: string;
  engineerId: string;
};

interface ProjectDealTeamFieldsProps {
  value: ProjectDealTeamFieldsValue;
  onChange: (next: ProjectDealTeamFieldsValue) => void;
  designersList: DesignerOption[];
  architectsList?: DesignerOption[];
  engineeringFirms: EngineeringFirm[];
  canEditDesigner?: boolean;
  canEditArchitect?: boolean;
  canEditEngineer?: boolean;
  engineerDisplay?: string;
  disabled?: boolean;
  compact?: boolean;
}

export const ProjectDealTeamFields: React.FC<ProjectDealTeamFieldsProps> = ({
  value,
  onChange,
  designersList,
  architectsList = [],
  engineeringFirms,
  canEditDesigner = true,
  canEditArchitect = true,
  canEditEngineer = true,
  engineerDisplay = '',
  disabled = false,
  compact = false,
}) => {
  const selectedFirm = engineeringFirms.find(f => f.id === value.engineerFirmId);

  return (
    <div
      className={`npf-root npf-team-fields${compact ? ' npf-root--compact npf-team-fields--compact' : ''}${disabled ? ' is-disabled' : ''}`}
    >
      <div className={`npf-team-grid${compact ? '' : ' npf-team-grid--stacked'}`}>
        <div className="npf-row">
          <label
            className="npf-label"
            title={compact ? 'In-house designer assigned to this project.' : undefined}
          >
            <PencilRuler size={13} aria-hidden />
            Designer
          </label>
          <select
            className="npf-input"
            value={value.designerUid}
            disabled={disabled || !canEditDesigner}
            onChange={e => onChange({ ...value, designerUid: e.target.value })}
            aria-label="Assigned designer"
          >
            <option value="">— Unassigned —</option>
            {designersList.map(d => (
              <option key={d.uid} value={d.uid}>
                {d.displayName}
              </option>
            ))}
          </select>
          {!compact && <div className="npf-help">In-house designer assigned to this project.</div>}
        </div>

        <div className="npf-row npf-row--architect">
          <label
            className="npf-label"
            title={compact ? 'Firm name, plus architects with portal access. Pending invites can be added before they accept.' : undefined}
          >
            <PenTool size={13} aria-hidden />
            Architect
          </label>
          <input
            type="text"
            className="npf-input"
            value={value.architectName}
            disabled={disabled || !canEditArchitect}
            onChange={e => onChange({ ...value, architectName: e.target.value })}
            placeholder="External firm…"
            aria-label="External architect organization"
          />
          {architectsList.length > 0 && (
            <select
              className={`npf-input${compact ? ' npf-input--tight-top' : ' npf-input-stacked'}`}
              value=""
              disabled={disabled || !canEditArchitect}
              aria-label="Add invited architect"
              onChange={e => {
                const uid = e.target.value;
                if (!uid || value.architectUids.includes(uid)) return;
                onChange({ ...value, architectUids: [...value.architectUids, uid] });
              }}
            >
              <option value="">{compact ? 'Add architect…' : 'Add invited architect…'}</option>
              {architectsList
                .filter((a) => !value.architectUids.includes(a.uid))
                .map((a) => (
                  <option key={a.uid} value={a.uid}>{a.displayName}</option>
                ))}
            </select>
          )}
          {value.architectUids.length > 0 && (
            <div className="npf-architect-chips">
              {value.architectUids.map((uid) => {
                const label = architectsList.find((a) => a.uid === uid)?.displayName || uid;
                return (
                  <button
                    key={uid}
                    type="button"
                    className="npf-architect-chip"
                    disabled={disabled || !canEditArchitect}
                    onClick={() => onChange({
                      ...value,
                      architectUids: value.architectUids.filter((id) => id !== uid),
                    })}
                  >
                    {label} ×
                  </button>
                );
              })}
            </div>
          )}
          {!compact && (
            <div className="npf-help">Type a firm name, and/or grant architects portal access. Pending invites appear before they accept.</div>
          )}
        </div>

        <div className="npf-row npf-row--engineer">
          <label
            className="npf-label"
            title={compact ? 'Engineer of record for structural / engineering work.' : undefined}
          >
            <HardHat size={13} aria-hidden />
            Engineer
          </label>
          {canEditEngineer && engineeringFirms.length > 0 ? (
            <>
              <select
                className="npf-input"
                value={value.engineerFirmId}
                disabled={disabled}
                onChange={e => onChange({ ...value, engineerFirmId: e.target.value, engineerId: '' })}
                aria-label="Engineering firm"
              >
                <option value="">— Unassigned —</option>
                {engineeringFirms.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
              {selectedFirm && (
                selectedFirm.engineers.length === 0 ? (
                  !compact && <div className="npf-help">No engineers in this firm yet.</div>
                ) : (
                  <select
                    className={`npf-input${compact ? ' npf-input--tight-top' : ' npf-input-stacked'}`}
                    value={value.engineerId}
                    disabled={disabled}
                    onChange={e => onChange({ ...value, engineerId: e.target.value })}
                    aria-label="Engineer of record"
                  >
                    <option value="">Select engineer…</option>
                    {selectedFirm.engineers.map(eng => (
                      <option key={eng.id} value={eng.id}>
                        {eng.name}
                      </option>
                    ))}
                  </select>
                )
              )}
              {!compact && (
                <div className="npf-help">Engineer of record for structural / engineering work.</div>
              )}
            </>
          ) : (
            <>
              <input
                type="text"
                className="npf-input"
                value={engineerDisplay || '—'}
                readOnly
                disabled
                aria-label="Engineer of record"
              />
              {!compact && (
                <div className="npf-help">Engineer of record for structural / engineering work.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
